"use client"

import { createDeferredPromise } from "@opensea/utils"
import type { WalletName } from "@solana/wallet-adapter-base"
import { useWallet } from "@solana/wallet-adapter-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { SOLANA_CHAIN_ID } from "../../constants"

/**
 * Used to connect a solana wallet with a callback that resolves when the wallet is connected
 */
export function useConnectSolanaWallet() {
  const {
    wallet,
    select: selectSolanaWallet,
    connect: connectSolanaWallet,
  } = useWallet()
  const selectSolanaWalletRef =
    useRef<(walletName: WalletName | null) => void>(selectSolanaWallet)
  useEffect(() => {
    selectSolanaWalletRef.current = selectSolanaWallet
  }, [selectSolanaWallet])

  const [solanaConnectingWallet, setSolanaConnectingWallet] = useState<string>()
  const solanaConnectPromiseRef = useRef(
    createDeferredPromise<{
      accounts: string[]
      chainId: number
    }>(),
  )

  useEffect(() => {
    if (
      solanaConnectingWallet &&
      solanaConnectingWallet === wallet?.adapter.name
    ) {
      connectSolanaWallet()
        .then(() => {
          solanaConnectPromiseRef.current.resolve({
            accounts: [wallet.adapter.publicKey?.toString() ?? ""],
            chainId: SOLANA_CHAIN_ID,
          })
        })
        .catch(solanaConnectPromiseRef.current.reject)
        .finally(() => {
          solanaConnectPromiseRef.current = createDeferredPromise()
          setSolanaConnectingWallet(undefined)
        })
    }
  }, [wallet, solanaConnectingWallet, connectSolanaWallet])

  return useCallback(async (connectorId: string) => {
    selectSolanaWalletRef.current(connectorId as WalletName)
    setSolanaConnectingWallet(connectorId)
    return solanaConnectPromiseRef.current.promise
  }, [])
}
